import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: 'user' | 'partner' | 'moderator' | 'admin';
  phone_number?: string;
  created_at: string;
  last_login?: string;
  status: string;
  image_quality_preference?: 'data-saver' | 'balanced' | 'high-quality' | 'automatic';
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = async (userId: string) => {
    console.log('AuthContext: Fetching user profile for:', userId);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        setUserProfile(null);
        return;
      }

      console.log('AuthContext: User profile fetched:', data);
      
      // Type-safe conversion with validation
      const profile: UserProfile = {
        ...data,
        image_quality_preference: (data.image_quality_preference === 'data-saver' || 
                                   data.image_quality_preference === 'balanced' || 
                                   data.image_quality_preference === 'high-quality' ||
                                   data.image_quality_preference === 'automatic')
          ? data.image_quality_preference
          : 'balanced'
      };
      
      setUserProfile(profile);
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      setUserProfile(null);
    }
  };

  useEffect(() => {
    console.log('AuthContext: Setting up auth listener');
    
    // Add timeout protection for initial loading
    const authTimeoutId = setTimeout(() => {
      console.warn('AuthContext: Auth timeout, setting loading to false');
      setLoading(false);
    }, 15000); // 15 second timeout
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event, session?.user?.id);
        clearTimeout(authTimeoutId);
        
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer profile fetching to avoid deadlock
        if (session?.user) {
          setTimeout(() => {
            fetchUserProfile(session.user.id).catch(error => {
              console.error('Error fetching user profile:', error);
              // Don't block auth flow if profile fetch fails
            });
          }, 0);
        } else {
          setUserProfile(null);
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('AuthContext: Initial session check:', !!session);
      clearTimeout(authTimeoutId);
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setTimeout(() => {
          fetchUserProfile(session.user.id).catch(error => {
            console.error('Error fetching user profile:', error);
          });
        }, 0);
      }
      
      setLoading(false);
    });

    return () => {
      clearTimeout(authTimeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName
        }
      }
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setUserProfile(null);
  };

  const isAdmin = userProfile?.role === 'admin';

  const value = {
    user,
    session,
    userProfile,
    loading,
    isAdmin,
    signUp,
    signIn,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};